/**
 * Bounded LRU with TTL — the core of `use-conversation-channel`'s dedupe
 * layer. realtime-collab-foundation Phase 4 Session 3 (2026-04-24).
 *
 * Each entry stores an `editId` -> insertion timestamp (ms). `has()` returns
 * true only for non-expired, non-evicted entries. `add()` inserts + triggers
 * an amortized TTL sweep so steady-state membership queries stay cheap.
 *
 * Contract:
 *  - Bounded by `maxSize` — oldest entry is evicted when cap is exceeded
 *    (Map iteration order is insertion order per ES2015, so `map.keys().next()`
 *    yields the oldest key deterministically).
 *  - Time-bounded by `ttlMs` — entries expire this long after insertion;
 *    `has()` lazy-evicts on read, `add()`'s `sweep()` eagerly prunes from the
 *    head.
 *  - Re-adding an existing `editId` refreshes its timestamp (treating the
 *    second add as the "most recent" observation — correct behaviour for
 *    SSE-first-then-Realtime ordering where the Realtime echo of a just-sent
 *    edit should extend the dedupe window rather than shortening it).
 *
 * NO persistent storage — each browser tab starts fresh on mount (a new tab
 * re-fetches via /resync anyway). Purely in-memory, per-hook-instance.
 */
export interface EditDedupeLRUOptions {
  maxSize: number;
  ttlMs: number;
}

export class EditDedupeLRU {
  private readonly map = new Map<string, number>();
  private readonly maxSize: number;
  private readonly ttlMs: number;

  constructor(options: EditDedupeLRUOptions) {
    this.maxSize = options.maxSize;
    this.ttlMs = options.ttlMs;
  }

  has(editId: string): boolean {
    const insertedAt = this.map.get(editId);
    if (insertedAt === undefined) return false;
    if (Date.now() - insertedAt > this.ttlMs) {
      this.map.delete(editId);
      return false;
    }
    return true;
  }

  add(editId: string): void {
    this.sweep();
    // Re-insert semantics: delete first so Map insertion order tracks the
    // most-recent add (keeps `maxSize` eviction pulling the genuinely oldest).
    if (this.map.has(editId)) this.map.delete(editId);
    this.map.set(editId, Date.now());
    if (this.map.size > this.maxSize) {
      const oldest = this.map.keys().next().value;
      if (oldest !== undefined) this.map.delete(oldest);
    }
  }

  clear(): void {
    this.map.clear();
  }

  size(): number {
    return this.map.size;
  }

  /**
   * Evict expired entries from the head of the Map. O(k) where k is the
   * count of expired entries; amortized O(1) per `add()` at steady state.
   * Stops at the first non-expired entry — safe because Map iteration order
   * is insertion order, and entries only gain freshness (via re-add's
   * delete+set), never lose it.
   */
  private sweep(): void {
    const cutoff = Date.now() - this.ttlMs;
    for (const [id, ts] of this.map) {
      if (ts < cutoff) this.map.delete(id);
      else break;
    }
  }
}
